import { StrictMode, useEffect, useMemo, useRef, useState } from 'react';
import { Root, createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import videojs, { VideoJsPlayer } from 'video.js';

import { useVideoHealthCheckForNativeHls } from './hooks/use-video-health-check-for-native-hls';
import { useVideoJsOptions } from './hooks/use-videojs-options';
import { PlayerUIConnector } from './player-ui.connector';
import { PlayerOptions } from './player.interface';
import { LoggerProvider } from '../../contexts/logger-context';
import { getI18nInstance } from '../../i18n/get-i18n-instance';
import { getPackageVersion } from '../../utils/version';
import { usePlayerColorsStyles } from '../user-interfaces/hooks/use-player-colors-styles';

import styles from './video.module.css';

type Props = {
  options: PlayerOptions;
};

const VideoJsComponent = videojs.getComponent('Component');

export function VideoJS({ options }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const rootRef = useRef<Root | null>(null);

  const i18nInstance = useMemo(() => getI18nInstance(options.lng), [options.lng]);

  const [videoJsElement, setVideoJsElement] = useState<Element | null>(null);

  const hasSubtitles = useMemo(() => {
    if (!options.tracks) {
      return false;
    }
    return options.tracks.some((track) => track.kind === 'subtitles' || track.kind === 'captions');
  }, [options.tracks]);

  const videoJsOptions = useVideoJsOptions(
    options.settings.maxPlaylistRetries,
    options.settings.looping,
    options.settings.muted,
    options.settings.aspectRatio,
    options.settings.videoLayout,
    options.settings.preloadSources
  );

  useVideoHealthCheckForNativeHls(playerRef.current);

  // Create / Update Video.js player
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    if (!playerRef.current) {
      const videoElement = containerRef.current.appendChild(document.createElement('video-js'));

      containerRef.current.appendChild(videoElement);
      playerRef.current = videojs(videoElement, videoJsOptions, () => {
        if (playerRef.current) {
          const vjsComponent = new VideoJsComponent(playerRef.current);
          playerRef.current.addChild(vjsComponent);

          setVideoJsElement(vjsComponent.el());
        }
      });
    }
  }, [videoJsOptions, options.poster]);

  // Render UI
  useEffect(() => {
    if (!videoJsElement || !options) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!videoJsElement || !options || !playerRef.current) {
        return;
      }

      if (!rootRef.current) {
        rootRef.current = createRoot(videoJsElement);
      }

      rootRef.current.render(
        <StrictMode>
          <I18nextProvider i18n={i18nInstance}>
            <LoggerProvider debug={options.debug}>
              <PlayerUIConnector player={playerRef.current} playerOptions={options} />
            </LoggerProvider>
          </I18nextProvider>
        </StrictMode>
      );
    });

    return () => {
      clearTimeout(timeoutId);
    };
  }, [options, videoJsElement, i18nInstance]);

  // Dispose Video.js player
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player) {
        player.dispose();
        playerRef.current = null;
      }

      setTimeout(() => {
        rootRef.current?.unmount();
        rootRef.current = null;
      });
    };
  }, []);

  const playerColorStyles = usePlayerColorsStyles({
    theme: options.settings.theme,
    customPrimaryColor: options.settings.primaryColor,
    controlBarColor: options.settings.controlBarColor,
    controlBarOpacity: options.settings.controlBarOpacity,
    iconColor: options.settings.iconColor,
    bigPlayButtonIconColor: options.settings.bigPlayButtonIconColor,
  });

  return (
    <div
      data-vjs-player
      data-movingimage-player
      data-testid="video-player"
      data-version={getPackageVersion()}
      {...(hasSubtitles ? { 'data-has-subtitles': 'true' } : {})}
      ref={containerRef}
      className={styles.wrapper}
      style={playerColorStyles}
    ></div>
  );
}
